/**
 * VPC (Virtual Private Cloud) API Service
 *
 * Service for managing customer VPCs - isolated virtual networks
 * automatically created for End Customers.
 */

import { apiClient } from './api'

const BASE_URL = '/api/v1/end-customer'

// ============================================
// Types
// ============================================

export type VPC = {
  id: string
  name: string
  description?: string
  vnet_id: string
  cidr: string
  gateway: string
  dns_servers?: string[]
  vni: number
  cluster_id: string
  cluster_name?: string
  enabled: boolean
  is_default?: boolean
  current_allocations: number
  max_allocations?: number
  extra_config?: {
    sdn_zone?: string
    snat_enabled?: boolean
    auto_created?: boolean
    vpc_type?: string
  }
  subnet?: {
    id: string
    cidr: string
    gateway: string
    available_ips?: number
  }
  created_at?: string
}

export type VPCListItem = {
  id: string
  name: string
  vnet_id: string
  cluster_id: string
  cidr: string
  gateway: string
  vni: number
  is_default: boolean
  enabled: boolean
  /**
   * Segunda flag de "esta VPC está de pé": as operações que escolhem uma VPC de
   * destino exigem `enabled` **e** `is_active`. Filtrar só por `enabled` oferece
   * ao cliente um destino que o backend recusa em seguida.
   */
  is_active?: boolean
  current_allocations: number
  /**
   * True quando esta VPC tem um Cloud Gateway atachado.
   * UI deve mostrar badge "Sem gateway" + CTA "Provisionar GW" quando false.
   */
  has_gateway?: boolean
  /**
   * Sinônimo invertido de has_gateway. Backend retorna ambos por clareza
   * na resposta de POST /vpc/create.
   */
  is_dangling?: boolean
  created_at?: string
}

export type VPCResponse = {
  has_vpc: boolean
  vpc: VPC | null
  message?: string
}

export type VPCListResponse = {
  vpcs: VPCListItem[]
  total: number
}

export type VPCCreateRequest = {
  vpc_name?: string
  custom_cidr?: string
}

export type VPCCreateResponse = {
  success: boolean
  vpc?: {
    id: string
    name: string
    vnet_id: string
    sdn_zone: string
    cidr: string
    gateway: string
    vni: number
    cluster_id: string
    cluster_name: string
    isolation_level?: string
    /** VPC nova nasce sem gateway (true até alguém provisionar). */
    has_gateway?: boolean
    is_dangling?: boolean
  }
  /** Aviso que VPC criada não tem GW — UI deve exibir e oferecer CTA. */
  warning?: string
  error?: string
  message?: string
}

export type VPCAssociatedVM = {
  id: string
  instance_name: string
  proxmox_vm_id: number
  status: string
  ip_addresses?: string[]
}

export type VPCDeleteResponse = {
  success: boolean
  error?: string
  message?: string
  warning?: string
  associated_vms?: VPCAssociatedVM[]
  /**
   * Consequências da migração que o cliente PRECISA ler (issue #804): VPC de
   * destino sem Cloud Gateway, VM que trocou de endereço fixo, DHCP que não pôde
   * ser reconfigurado agora. A exclusão foi bem-sucedida com todas elas.
   */
  warnings?: string[]
  /** Nomes das VMs cuja rede foi migrada para a VPC de destino. */
  migrated_vms?: string[]
}

export type VPCAllocatedIPsResponse = {
  vpc_id: string
  vpc_name: string
  cidr: string
  gateway: string
  current_allocations: number
  max_allocations?: number
  allocated_ips: {
    ip: string
    vm_id?: string
    vm_name?: string
    allocated_at?: string
  }[]
}

export type VPCUpdateResponse = {
  success: boolean
  vpc?: {
    id: string
    name: string
    cidr: string
    gateway: string
    vnet_id: string
    vni: number
  }
  warnings?: string[]
  error?: string
  message?: string
}

// ============================================
// API Functions
// ============================================

/**
 * Get the default VPC for the current customer
 */
export const getCustomerVPC = async (): Promise<VPCResponse> => {
  return apiClient.get<VPCResponse>(`${BASE_URL}/vpc`)
}

/**
 * List all VPCs for the current customer
 */
export const listCustomerVPCs = async (): Promise<VPCListResponse> => {
  return apiClient.get<VPCListResponse>(`${BASE_URL}/vpc/list`)
}

/**
 * Create a new VPC
 */
export const createVPC = async (data?: VPCCreateRequest): Promise<VPCCreateResponse> => {
  const params = new URLSearchParams()
  if (data?.vpc_name) {
    params.append('vpc_name', data.vpc_name)
  }
  if (data?.custom_cidr) {
    params.append('custom_cidr', data.custom_cidr)
  }

  const url = params.toString()
    ? `${BASE_URL}/vpc/create?${params.toString()}`
    : `${BASE_URL}/vpc/create`

  return apiClient.post<VPCCreateResponse>(url)
}

/**
 * Delete a VPC
 *
 * Com recursos conectados, `migrateToVpcId` é obrigatório: o backend migra a
 * REDE deles para a VPC de destino (endereço fixo realocado, cloud-init
 * reescrito, DHCP do gateway reconfigurado) antes de apagar a VNET (#804).
 *
 * `confirmNoGateway` libera um destino sem Cloud Gateway — sem ele o backend
 * recusa com 409 e `error.code === 'vpc_without_gateway'`, porque ali as VMs
 * ficam sem DHCP e sem internet (issue #727).
 */
export const deleteVPC = async (
  vpcId: string,
  migrateToVpcId?: string,
  options?: { confirmNoGateway?: boolean },
): Promise<VPCDeleteResponse> => {
  const params = new URLSearchParams()
  if (migrateToVpcId) params.set('migrate_to_vpc_id', migrateToVpcId)
  // Só quando confirmado: mandar `false` explícito é ruído no log de acesso e
  // não muda o default do backend.
  if (options?.confirmNoGateway) params.set('confirm_no_gateway', 'true')
  const qs = params.toString()
  return apiClient.delete<VPCDeleteResponse>(`${BASE_URL}/vpc/${vpcId}${qs ? `?${qs}` : ''}`)
}

/**
 * Update the CIDR of a VPC
 */
export const updateVPCCidr = async (vpcId: string, newCidr: string): Promise<VPCUpdateResponse> => {
  const params = new URLSearchParams({ new_cidr: newCidr })
  return apiClient.put<VPCUpdateResponse>(`${BASE_URL}/vpc/${vpcId}?${params.toString()}`)
}

/**
 * Get allocated IPs for a VPC
 */
export const getVPCAllocatedIPs = async (vpcId: string): Promise<VPCAllocatedIPsResponse> => {
  return apiClient.get<VPCAllocatedIPsResponse>(`${BASE_URL}/vpc/${vpcId}/ips`)
}

// ============================================
// Default Export
// ============================================

export const vpcApi = {
  getCustomerVPC,
  listCustomerVPCs,
  createVPC,
  updateVPCCidr,
  deleteVPC,
  getVPCAllocatedIPs,
}

export default vpcApi
